import React, { useState, useRef, useEffect } from 'react';
import { connect } from 'react-redux';
import classnames from 'classnames';
import UserSources from '../../sources/user';
import translations from '../../translations';
import { isNullOrEmpty, selectStyles } from '../../utils/core';
import { addTenantAndOrUser } from '../../actions/reduxActions/organizations';
import { addNotification } from '../../actions/reduxActions/notification';
import { REGEX_PATTERN_TENANT_NAME, REGEX_ILLEGAL_TENANT_NAME, REGEX_EMAIL, SYSTEM_ROLES } from '../../constants';
import CreatableSelect from 'react-select/creatable';

const isValidNewEmail = inputValue => REGEX_EMAIL.test(inputValue.trim());

/**
 * @description Flow Org. provision a new tenant or a new user
 *
 * Display the form and button used to provision a new tenant or a new user.
 */
export const Provision = ({ currentUser, addTenantAndOrUserAction, closeModal, addNotificationAction }) => {
    const formRef = useRef(null);

    const role = SYSTEM_ROLES.administrator.developerName;
    const [tenantName, setTenantName] = useState(null);
    const [selectedEmails, setSelectedEmails] = useState([]);
    const [userOptions, setUserOptions] = useState([]);

    const validateAndSetTenantName = ({ target }) => {
        const { value, validity: { patternMismatch } } = target;

        if (patternMismatch) {
            const invalidChars = value
                .match(REGEX_ILLEGAL_TENANT_NAME)
                .map(char => char === ' ' ? '"space"' : `"${char}"`)
                .join(', ');
            target.setCustomValidity(`Characters ${invalidChars} are not permitted in tenant names`);
        } else {
            target.setCustomValidity('');
        }
        setTenantName(value);
    };

    const clearProvisionData = () => {
        if (formRef.current) {
            formRef.current.reset();
        }
        setTenantName(null);
        setSelectedEmails([]);
        setUserOptions([]);
    };

    useEffect(() => {
        const { authenticationToken, tenantId } = currentUser;
        UserSources.getAllUsersForCurrentTenant({ authenticationToken, tenantId }).then(
            (response) => {
                const newUserOptions = response.map(user => ({ label: user.email, value: user.email }));
                setUserOptions(newUserOptions);
                setSelectedEmails(newUserOptions);
            },
        ).catch(error => addNotificationAction({
            type: 'error',
            message: error.message,
            isPersistent: true,
        }));
    }, []);

    return (
        <>
            <p>
                {translations.FORG_provision_description_1}
                &nbsp;
                {translations.FORG_provision_description_2}
                &nbsp;
                {translations.FORG_provision_description_3}
            </p>
            <form
                ref={formRef}
                onSubmit={
                    (event) => {
                        event.preventDefault();
                        addTenantAndOrUserAction({
                            authenticationToken: currentUser.authenticationToken,
                            provisionData: {
                                developerName: tenantName,
                                users: selectedEmails.map(email => ({
                                    email: email.value,
                                    role,
                                })),
                            },
                        });
                        clearProvisionData();
                        closeModal();
                    }
                }
            >
                <div className="form-group">
                    <label>
                        Email
                        <span className="input-required"> *</span>
                    </label>
                    <CreatableSelect
                        isMulti
                        className="full-width"
                        styles={selectStyles}
                        isValidNewOption={isValidNewEmail}
                        options={userOptions}
                        onChange={newEmails => setSelectedEmails(newEmails)}
                        formatCreateLabel={input => `Add ${input}`}
                        noOptionsMessage={input => input.inputValue.length > 0 ? `'${input.inputValue}' is not a valid email address` : 'No results found'}
                    />
                    <label className="help-block">
                        Select an email, or type a new one and click "Add".
                    </label>
                </div>

                <div className="form-group">
                    <label>
                        Tenant name
                        <span className="input-required"> *</span>
                    </label>
                    <input
                        maxLength="255"
                        required
                        pattern={REGEX_PATTERN_TENANT_NAME}
                        className="form-control admin-input tenant-name"
                        type="text"
                        onInput={validateAndSetTenantName}
                    />
                    <label className="help-block">
                        Only letters, numbers, "-" and "." are permitted.
                    </label>
                </div>
                <footer className="form-footer">
                    <button type="button" className="btn btn-sm btn-default" onClick={() => closeModal()}>Cancel</button>
                    <button
                        type="submit"
                        className="btn btn-sm btn-success"
                        // At least one email must be selected
                        // The tenant name cannot be empty
                        disabled={selectedEmails.length === 0 || isNullOrEmpty(tenantName)}
                    >
                        Create
                    </button>
                </footer>
            </form>
        </>
    );
};

const mapDispatchToProps = {
    addTenantAndOrUserAction: addTenantAndOrUser,
    addNotificationAction: addNotification,
};

export default connect(null, mapDispatchToProps)(Provision);
